import React, { useEffect, useRef, useState } from 'react';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from './components/ui/collapsible';
import './Citations.css';

const SourceIcon = ({ type }) => (
  type === 'tenant' ? (
    <svg width="12" height="12" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  ) : (
    <svg width="12" height="12" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  )
);

const getSourceLabel = (answerSource, hasTenantDocs) => {
  if (answerSource === 'tenant_docs') return 'Based on your implementation docs';
  if (answerSource === 'oracle_kb') return 'Based on Oracle documentation';
  if (answerSource === 'hybrid') return 'Based on Oracle docs + AI knowledge';
  if (answerSource === 'ai_knowledge') return 'Based on AI knowledge';
  if (hasTenantDocs) return 'Based on your implementation docs';
  return 'Based on Oracle HCM knowledge base';
};

const getAnswerSourceBadgeLabel = (answerSource, hasTenantDocs) => {
  if (answerSource === 'tenant_docs' || hasTenantDocs) return 'From your documents';
  if (answerSource === 'oracle_kb') return 'Oracle Knowledge Base';
  if (answerSource === 'hybrid') return 'Mixed sources';
  if (answerSource === 'ai_knowledge') return 'General Oracle knowledge';
  return null;
};

const getScoreLabel = (score) => {
  if (score >= 0.85) return 'High relevance';
  if (score >= 0.65) return 'Good match';
  return 'Related';
};

const getFilename = (filename) => {
  if (!filename) return 'Unknown source';
  return filename.length > 35
    ? filename.substring(0, 35) + '...'
    : filename;
};

const CitationRow = ({ citation, sourceType }) => {
  const barRef = useRef(null);
  const scorePct = Math.round(citation.score * 100);
  const scale = citation.score;

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      bar.style.transform = `scaleX(${scale})`;
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      bar.style.transform = `scaleX(${scale})`;
    });
    return () => cancelAnimationFrame(frame);
  }, [scale]);

  const statusLabel = citation.is_grounded
    ? '✓ Verified'
    : getScoreLabel(citation.score);

  return (
    <div className={`citation-item citation-${sourceType}`}>
      <div className="citation-row-summary">
        <div className="citation-icon" aria-hidden="true">
          <SourceIcon type={sourceType} />
        </div>
        <span className="citation-filename">
          {getFilename(citation.filename)}
        </span>
        <div className="citation-bar-track" aria-hidden="true">
          <div ref={barRef} className="citation-bar-fill" />
        </div>
        <span className="citation-score">{scorePct}%</span>
        <span
          className={`citation-status ${
            citation.is_grounded ? 'citation-status-verified' : 'citation-status-good'
          }`}
        >
          {statusLabel}
        </span>
      </div>
      {sourceType === 'oracle' && (
        <div className="citation-meta">
          {citation.release
            ? `Oracle HCM ${citation.release}`
            : 'Oracle HCM documentation'}
        </div>
      )}
      {citation.chunk_preview && (
        <p className="citation-preview">"{citation.chunk_preview}…"</p>
      )}
    </div>
  );
};

const Citations = ({ citations, hasTenantDocs, answerSource }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!citations || citations.length === 0) return null;

  const tenantCitations = citations.filter(
    (c) => c.source_type === 'tenant'
  );
  const oracleCitations = citations.filter(
    (c) => c.source_type === 'oracle'
  );

  const badgeLabel = getAnswerSourceBadgeLabel(answerSource, hasTenantDocs);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="citations-container">
      <CollapsibleTrigger asChild>
        <button type="button" className="citations-toggle">
          <span className="citations-toggle-icon">
            {isOpen ? '▾' : '▸'}
          </span>
          <span className="citations-label">
            {getSourceLabel(answerSource, hasTenantDocs)}
          </span>
          {badgeLabel && (
            <span className="answer-source-badge">{badgeLabel}</span>
          )}
          <span className="citations-count">
            {citations.length} source{citations.length !== 1 ? 's' : ''}
          </span>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="citations-list">
        {tenantCitations.length > 0 && (
          <div className="citations-group">
            <div className="citations-group-label">
              Your implementation docs
            </div>
            {tenantCitations.map((c, i) => (
              <CitationRow key={i} citation={c} sourceType="tenant" />
            ))}
          </div>
        )}

        {oracleCitations.length > 0 && (
          <div className="citations-group">
            <div className="citations-group-label">
              Oracle HCM documentation
            </div>
            {oracleCitations.map((c, i) => (
              <CitationRow key={i} citation={c} sourceType="oracle" />
            ))}
          </div>
        )}

        {!hasTenantDocs && answerSource !== 'tenant_docs' && (
          <div className="citations-no-tenant">
            <span>💡</span>
            Upload your implementation docs anytime for answers tailored to your setup
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default Citations;
